import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
/* Components */
import { ContentSection } from 'site-modules/shared/components/content-section/content-section';
/* Utils */
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { formatPriceString } from 'site-modules/shared/utils/price-utils';
import { getStaticImageUrl } from 'client/utils/image-helpers';
import { getSelectedOffer } from 'client/site-modules/shared/utils/financing/financing';
/* Models */
import { FinancingModel, PATHS } from 'client/data/models/financing';
/* Constants */
import {
  EXCLUDES_TAXES_AND_FEES,
  FINANCING_DISCLAIMER,
  INCLUDES_TAXES_AND_FEES,
} from 'client/site-modules/shared/constants/financing/constants';
import { DEFAULT_DOWN_PAYMENT } from 'site-modules/shared/constants/financing/financing';

import './capital-one-personalized-payment.scss';

function CapitalOnePersonalizedPaymentUI({ vinData = {}, financingOffers = [], calculatorSelections = {} }) {
  const financingOffer = {
    ...calculatorSelections,
    ...getSelectedOffer(financingOffers, calculatorSelections),
  };

  const LOGO_SRC = getStaticImageUrl('/img/car-buying/capital1/financing_icon_preq.svg');
  const URL = `/financing/${vinData.vin}/payments`;
  const CONTENT = {
    TITLE: "You've been prequalified!",
    SUBTITLE:
      "You've been prequalified for a loan from Capital One. Now let's personalize your payments for this vehicle.",
    CTA_TEXT: 'Personalize payments',
  };

  const monthlyPayment = get(financingOffer, 'financeInformation.monthlyPayment', 0);
  const apr = get(financingOffer, 'financeInformation.buyRate');
  const term = get(financingOffer, 'financeInformation.loanTerm');
  const downPayment = get(financingOffer, 'financeInformation.downPayment', DEFAULT_DOWN_PAYMENT);
  const zipCode = get(financingOffer, 'vehicleStructure.taxesAndFees.zipCode');
  const styleId = get(vinData, 'vehicleInfo.styleInfo.styleId');

  return (
    <ContentSection
      className="bg-cool-gray-90 capital-one-personalized-payment-wrapper px-1 pt-1_5 pb-1 mb-1_5"
      sectionTitle="Your Capital One Prequalification "
      dataTrackingParent="financing_cta-calculator"
      titleClassName="section-title heading-4 mb-1_5"
      line={false}
    >
      <div className="capital-one-personalized-payment pos-r bg-white">
        <div className="img-container bg-white pos-a">
          <img className="d-block mx-auto my-0" src={LOGO_SRC} alt="Capital One" />
        </div>

        <div className="heading-3 text-center header">{CONTENT.TITLE}</div>

        <div className="personalize-payments py-1 px-1_5 my-1 mx-1 mx-md-auto text-center">
          <div className="fw-medium mb-0_75">Your Prequalified Payment from Capital One</div>
          {monthlyPayment && (
            <div className="size-30 text-primary-darker fw-bold">
              {formatPriceString(monthlyPayment, 2, 2)}
              <span className="size-16">/mo</span>
            </div>
          )}

          <div className="small">
            {apr}% APR<span className="size-14 px-0_5 fw-bold">&#183;</span>
            {term} mo<span className="size-14 px-0_5 fw-bold">&#183;</span>
            {downPayment && formatPriceString(downPayment)} Down
          </div>
          <div className="small">{zipCode && styleId ? INCLUDES_TAXES_AND_FEES : EXCLUDES_TAXES_AND_FEES}</div>
          <hr className="my-0_75" />
          <div className="size-12 text-cool-gray-50 ">{FINANCING_DISCLAIMER}</div>
        </div>

        <div className="px-1 px-md-1_5 text-center mx-md-2_5">{CONTENT.SUBTITLE}</div>

        <div className="d-flex">
          <a
            href={URL}
            rel="noopener noreferrer"
            className="btn text-capitalize text-primary-darker btn-outline-primary-b btn-sm px-1_25 size-16 mt-1_5 mb-2 text-transform-none fw-normal mx-auto personalize-payment-btn"
            data-tracking-id="financing_cta"
            data-tracking-value={CONTENT.CTA_TEXT}
          >
            <span>{CONTENT.CTA_TEXT}</span>
          </a>
        </div>
      </div>
    </ContentSection>
  );
}

CapitalOnePersonalizedPaymentUI.propTypes = {
  vinData: PropTypes.shape({}),
  financingOffers: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({})), PropTypes.shape({})]),
  calculatorSelections: PropTypes.shape({}),
};

const stateToPropsConfig = {
  calculatorSelections: bindToPath(
    ({ vinData }) => PATHS.CALCULATOR_SELECTIONS({ vin: get(vinData, 'vin') }),
    FinancingModel
  ),
};

export const CapitalOnePersonalizedPayment = connectToModel(CapitalOnePersonalizedPaymentUI, stateToPropsConfig);
